import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CreditCard, Send, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { contactInfo } from '../data';

export default function Contact() {
  const [activeTab, setActiveTab] = useState('priser');
  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    telefon: '',
    besked: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Honeypot check: if filled, silently ignore or reject
    if (formData.website_url) {
      // Treat as successful to fool bots, but don't actually submit
      setTimeout(() => {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({ navn: '', email: '', telefon: '', besked: '', website_url: '' });
      }, 1000);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          navn: formData.navn,
          email: formData.email,
          telefon: formData.telefon,
          besked: formData.besked,
          website_url: formData.website_url // Must include the honeypot field in the payload
        })
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({ navn: '', email: '', telefon: '', besked: '', website_url: '' });
      } else {
        throw new Error('Der opstod en fejl ved afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en netværksfejl. Prøv igen senere.'
      });
    }
  };

  return (
    <section id="kontakt" className="pt-16 pb-0 md:py-20 bg-white md:bg-[#F7F4F1] relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
          <h2 className="font-serif-elegant text-4xl md:text-5xl text-[#4A4A4A] font-light mb-6">
            Tag det første skridt
          </h2>
          <div className="w-12 h-[1px] bg-[#BFC8BE] mx-auto mb-6" />
          <p className="font-sans-clean text-base text-[#4A4A4A]/70 leading-relaxed">
            Du er altid velkommen til at kontakte mig for en uforpligtende samtale, tidsbestilling eller hvis du har spørgsmål til forløbet.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Contact Information Column */}
          <div className="lg:col-span-5 text-left space-y-10">
            
            {/* Contact Cards */}
            <div className="space-y-6">
              <h3 className="font-serif-elegant text-2xl text-[#4A4A4A] font-light mb-4">
                Kontaktoplysninger
              </h3>
              
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#EDE6DF]">
                  <Phone size={16} className="text-[#BFC8BE]" />
                </div>
                <div>
                  <p className="font-sans-clean text-[10px] tracking-widest uppercase text-[#4A4A4A]/50 mb-0.5">Telefon</p>
                  <a href={`tel:${contactInfo.phone}`} className="font-sans-clean text-sm text-[#4A4A4A] hover:text-[#BFC8BE] transition-colors font-medium">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#EDE6DF]">
                  <Mail size={16} className="text-[#BFC8BE]" />
                </div>
                <div>
                  <p className="font-sans-clean text-[10px] tracking-widest uppercase text-[#4A4A4A]/50 mb-0.5">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="font-sans-clean text-sm text-[#4A4A4A] hover:text-[#BFC8BE] transition-colors font-medium">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#EDE6DF]">
                  <MapPin size={16} className="text-[#BFC8BE]" />
                </div>
                <div>
                  <p className="font-sans-clean text-[10px] tracking-widest uppercase text-[#4A4A4A]/50 mb-0.5">Klinik</p>
                  <p className="font-sans-clean text-sm text-[#4A4A4A] font-medium">
                    {contactInfo.address.startsWith("Behandlerkollektivet,") ? (
                      <>
                        Behandlerkollektivet, <br />
                        {contactInfo.address.replace("Behandlerkollektivet,", "").trim()}
                      </>
                    ) : (
                      contactInfo.address
                    )}, {contactInfo.zipCity}
                  </p>
                </div>
              </div>

              {contactInfo.cvr && (
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-[#EDE6DF]">
                    <FileText size={16} className="text-[#BFC8BE]" />
                  </div>
                  <div>
                    <p className="font-sans-clean text-[10px] tracking-widest uppercase text-[#4A4A4A]/50 mb-0.5">CVR-nummer</p>
                    <p className="font-sans-clean text-sm text-[#4A4A4A] font-medium">
                      {contactInfo.cvr}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing & Practical Info Tabs */}
            <div className="space-y-6 border-t border-[#EDE6DF] pt-8">
              <div className="flex border-b border-[#EDE6DF]">
                <button
                  type="button"
                  onClick={() => setActiveTab('priser')}
                  className={`pb-3 text-sm font-sans-clean tracking-wider uppercase transition-all duration-300 relative focus:outline-none mr-6 cursor-pointer ${
                    activeTab === 'priser'
                      ? 'text-[#4A4A4A] font-semibold'
                      : 'text-[#4A4A4A]/50 hover:text-[#4A4A4A]'
                  }`}
                >
                  Priser
                  {activeTab === 'priser' && (
                    <motion.div
                      layoutId="contactActiveIndicator"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#BFC8BE]"
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('praktisk')}
                  className={`pb-3 text-sm font-sans-clean tracking-wider uppercase transition-all duration-300 relative focus:outline-none cursor-pointer ${
                    activeTab === 'praktisk'
                      ? 'text-[#4A4A4A] font-semibold'
                      : 'text-[#4A4A4A]/50 hover:text-[#4A4A4A]'
                  }`}
                >
                  Praktisk info
                  {activeTab === 'praktisk' && (
                    <motion.div
                      layoutId="contactActiveIndicator"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#BFC8BE]"
                    />
                  )}
                </button>
              </div>

              <div className="min-h-[140px]">
                <AnimatePresence mode="wait">
                  {activeTab === 'priser' ? (
                    <motion.div
                      key="priser"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-3 text-sm font-sans-clean"
                    >
                      <div className="flex justify-between border-b border-[#EDE6DF]/40 pb-2">
                        <span className="text-[#4A4A4A]/70">Individuel terapi</span>
                        <span className="text-[#4A4A4A] font-medium">{contactInfo.pricing.individual}</span>
                      </div>
                      <div className="flex justify-between border-b border-[#EDE6DF]/40 pb-2">
                        <span className="text-[#4A4A4A]/70">Parterapi</span>
                        <span className="text-[#4A4A4A] font-medium">{contactInfo.pricing.couple}</span>
                      </div>
                      <div className="flex justify-between border-b border-[#EDE6DF]/40 pb-2">
                        <span className="text-[#4A4A4A]/70">Familieterapi</span>
                        <span className="text-[#4A4A4A] font-medium">{contactInfo.pricing.family}</span>
                      </div>
                      <div className="flex justify-between border-b border-[#EDE6DF]/40 pb-2">
                        <span className="text-[#4A4A4A]/70">Supervision</span>
                        <span className="text-[#4A4A4A] font-medium">{contactInfo.pricing.supervision}</span>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="praktisk"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5 text-sm font-sans-clean text-left"
                    >
                      <div className="space-y-1">
                        <h4 className="font-semibold text-[#4A4A4A] flex items-center gap-2 text-xs uppercase tracking-wider">
                          <Clock size={14} className="text-[#BFC8BE]" />
                          Afbudsregler
                        </h4>
                        <p className="text-[#4A4A4A]/80 leading-relaxed text-xs">
                          Afbud meldes senest dagen før inden kl. 12 på mail, sms eller telefon. Herefter afregnes fuld pris for, for sent afbud eller udeblivelse.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <h4 className="font-semibold text-[#4A4A4A] flex items-center gap-2 text-xs uppercase tracking-wider">
                          <CreditCard size={14} className="text-[#BFC8BE]" />
                          Betaling
                        </h4>
                        <p className="text-[#4A4A4A]/80 leading-relaxed text-xs">
                          Betaling sker via fremsendt faktura. Betalingsfrist er 5 dage fra udstedelsesdato og betalingen overføres til bankkonto i Lån og Spar Bank. Kontonummeret vil fremgå af fakturaen og du bedes anføre fakturanummeret på overførslen.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

          </div>

          {/* Contact Form Column */}
          <div className="lg:col-span-7">
            <div className="bg-white -mx-6 md:mx-0 rounded-none md:rounded-3xl p-6 md:p-12 shadow-sm border-t border-b-0 border-x-0 md:border-t md:border-x md:border-b-0 border-[#EDE6DF]/40 text-left">
              <h3 className="font-serif-elegant text-2xl text-[#4A4A4A] font-light mb-2">
                Send en besked
              </h3>
              <p className="font-sans-clean text-xs text-[#4A4A4A]/60 mb-8">
                Udfyld formularen nedenfor, og jeg vil vende tilbage til dig hurtigst muligt – typisk inden for 24 timer på hverdage.
              </p>

              {status.success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#BFC8BE]/10 border border-[#BFC8BE]/30 rounded-2xl p-8 text-center flex flex-col items-center"
                >
                  <CheckCircle2 size={48} className="text-[#BFC8BE] mb-4" />
                  <h4 className="font-serif-elegant text-2xl text-[#4A4A4A] font-light mb-2">Tak for din besked</h4>
                  <p className="font-sans-clean text-sm text-[#4A4A4A]/70 max-w-md leading-relaxed">
                    Jeg har modtaget din henvendelse og vil læse den med stor fortrolighed. Jeg vender tilbage til dig så hurtigt som muligt.
                  </p>
                  <button
                    onClick={() => setStatus({ submitting: false, success: false, error: null })}
                    className="mt-6 px-6 py-2.5 border border-[#BFC8BE] text-[#BFC8BE] hover:bg-[#BFC8BE] hover:text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300"
                  >
                    Send en ny besked
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                  <div className="sr-only absolute overflow-hidden w-0 h-0" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke dette felt)</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="flex flex-col gap-2">
                      <label htmlFor="navn" className="font-sans-clean text-xs tracking-wider uppercase text-[#4A4A4A]/70 font-semibold">
                        Dit navn *
                      </label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="F.eks. Anna Hansen"
                        className="w-full px-4 py-3 bg-transparent border border-[#EDE6DF] rounded-lg focus:outline-none focus:border-[#BFC8BE] focus:bg-white text-sm text-[#4A4A4A] transition-colors"
                      />
                    </div>

                    {/* Phone */}
                    <div className="flex flex-col gap-2">
                      <label htmlFor="telefon" className="font-sans-clean text-xs tracking-wider uppercase text-[#4A4A4A]/70 font-semibold">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        required
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="F.eks. 12 34 56 78"
                        className="w-full px-4 py-3 bg-transparent border border-[#EDE6DF] rounded-lg focus:outline-none focus:border-[#BFC8BE] focus:bg-white text-sm text-[#4A4A4A] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="font-sans-clean text-xs tracking-wider uppercase text-[#4A4A4A]/70 font-semibold">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="F.eks. anna@mail.dk"
                      className="w-full px-4 py-3 bg-transparent border border-[#EDE6DF] rounded-lg focus:outline-none focus:border-[#BFC8BE] focus:bg-white text-sm text-[#4A4A4A] transition-colors"
                    />
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-2">
                    <label htmlFor="besked" className="font-sans-clean text-xs tracking-wider uppercase text-[#4A4A4A]/70 font-semibold">
                      Hvad kan jeg hjælpe dig med? *
                    </label>
                    <textarea
                      id="besked"
                      name="besked"
                      required
                      rows="5"
                      value={formData.besked}
                      onChange={handleChange}
                      placeholder="Skriv kort om din situation eller dit ønske om forløb..."
                      className="w-full px-4 py-3 bg-transparent border border-[#EDE6DF] rounded-lg focus:outline-none focus:border-[#BFC8BE] focus:bg-white text-sm text-[#4A4A4A] transition-colors resize-none"
                    />
                  </div>

                  {/* Error State */}
                  {status.error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700 text-sm">
                      <AlertCircle size={18} className="flex-shrink-0" />
                      <p>{status.error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="w-full py-4 bg-[#BFC8BE] hover:bg-[#BFC8BE]/90 disabled:bg-[#BFC8BE]/50 text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {status.submitting ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-t-white border-r-transparent border-b-transparent border-l-transparent animate-spin" />
                        Sender besked...
                      </>
                    ) : (
                      <>
                        Send besked
                        <Send size={12} />
                      </>
                    )}
                  </button>

                  <p className="text-[11px] text-[#4A4A4A]/50 text-center leading-relaxed">
                    * Ved at indsende formularen accepterer du, at dine oplysninger behandles fortroligt i overensstemmelse med gældende GDPR-regler for sundhedsfaglige personer. Undgå venligst at skrive dybt personfølsomme oplysninger i selve formularen.
                  </p>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
